// @ts-nocheck
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import { CmsLibraryAssetFolderFacetModel } from "@store/Framework/Types/ApiModels";
import React, { FC } from "react";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import FolderOutline from "@store/Library/Components/Icons/FolderOutline";
import FolderPlusOutline from "@store/Library/Components/Icons/FolderPlusOutline";
import getColor from "@insite/mobius/utilities/getColor";

interface OwnProps {
    folder: CmsLibraryAssetFolderFacetModel;
    onFacetSelection: (id: string, checked: boolean) => void;
    selectedId: string | undefined;
    depth: number;
}

type Props = OwnProps;

interface SubFolderFacetsStyles {
    checkBoxGroup?: CheckboxGroupProps;
    checkbox?: CheckboxProps;
    subFolderFacetWrapper?: InjectableCss;
}

const fontForDepth = (depth: number) => {
    const weightByDepth = [
        (x: any) => x.theme.design.typography.weight.semibold,
        (x: any) => x.theme.design.typography.weight.base,
    ];
    const idx = Math.min(depth, 1);
    return { weight: weightByDepth[idx] };
};

export const subFolderFacetsStyles: SubFolderFacetsStyles = {
    checkBoxGroup: {
        css: css`
            margin-left: ${pxToRem(15)};
            padding-bottom: ${pxToRem(10)};
        `,
    },
    checkbox: {
        css: css`
            /* font-weight: ${x => x.theme.design.typography.weight.semibold}; */
            align-items: center;

            &:hover {
                text-decoration: underline;
            }

            span[role="checkbox"] {
                border: none;
                margin-top: 0;

                &[aria-checked="true"]:not([aria-disabled="true"]),
                &[aria-checked="mixed"]:not([aria-disabled="true"]) {
                    background: none;
                    border: 0;

                    svg {
                        color: ${getColor("primary")};
                    }
                }

                &[aria-checked="true"]:not([aria-disabled="true"]) + label {
                    color: ${getColor("primary")};
                }

                &[aria-disabled="true"] {
                    background: none;
                    border: none;
                }
                &[aria-checked="false"] svg {
                    display: block;
                }
                & + label {
                    margin-left: 10px;
                    font-size: ${x => x.theme.design.typography.size.tiny};
                }
                &:focus {
                    outline: none;
                    box-shadow: 0;
                }
                svg {
                    color: ${getColor("text")};
                }
            }

            ${breakpoint("xl")} {
                > span[role="checkbox"] + label {
                    font-size: ${x => x.theme.design.typography.size.smaller};
                }
            }
        `,
        iconProps: {
            size: 20,
        },
        mergeCss: true,
    },
    subFolderFacetWrapper: {
        css: css`
            padding-top: ${pxToRem(15)};
        `,
    },
};

const styles = subFolderFacetsStyles;

const SubFolderFacets: FC<Props> = ({ folder, selectedId, onFacetSelection, depth }) => {
    if (!folder) {
        return null;
    }

    const { weight } = fontForDepth(depth);

    const checkboxStyles: CheckboxProps = {
        ...styles.checkbox,
        css: css`
            ${styles.checkbox?.css}
            font-weight: ${weight};
        `,
    };

    const handleChange = (value: boolean, id: string) => {
        onFacetSelection(id, value);
    };

    const folderIcon = folder.subFolderFacets.length > 0 ? FolderPlusOutline : FolderOutline;

    return (
        <>
            <CheckboxGroup {...styles.checkBoxGroup}>
                <Checkbox
                    onChange={(_, currentValue) => handleChange(currentValue, folder.id)}
                    checked={selectedId === folder.id}
                    {...checkboxStyles}
                    iconProps={{ ...styles.checkbox?.iconProps, src: folderIcon }}
                >
                    {folder.shortDescription}
                </Checkbox>
                {folder.subFolderFacets.length > 0 && (
                    <StyledWrapper {...styles.subFolderFacetWrapper}>
                        {folder.subFolderFacets.map(subfolder => (
                            <SubFolderFacets
                                key={subfolder.id}
                                folder={subfolder}
                                onFacetSelection={onFacetSelection}
                                selectedId={selectedId}
                                depth={depth + 1}
                            />
                        ))}
                    </StyledWrapper>
                )}
            </CheckboxGroup>
        </>
    );
};

export default SubFolderFacets;
